import React, { useState } from 'react';
import { Box, Button, Typography } from '@heycater/design-system';
import { Grid, useMediaQuery } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { Field, Form, Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  CUSTOMER_LEAD_RECORD_TYPE,
  PHONE_NUMBER_REGEX,
} from 'caterers/constants';
import { webToLeadData } from 'caterers/services/webToLeadData';
import Bugsnag from 'lib/bugsnag';
import { Mouseflow } from 'lib/mouseflow';
import Yup from 'lib/yup';
import FormikFormField from 'shared/components/FormikFormField';
import { webToLeadRequest } from 'shared/services/webToLeadRequest';

const HIDE_SUCCESS_MESSAGE_MILLIS = 5000;
const FORM_ID = 'customer-landing-page-request-form';

function RequestForm() {
  const { t } = useTranslation('static_pages');
  const [contactFormSubmitted, setContactFormSubmitted] = useState(false);
  const mobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const hideSuccessMessage = setTimeout(() => {
    setContactFormSubmitted(false);
  }, HIDE_SUCCESS_MESSAGE_MILLIS);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      Mouseflow.notify.formSubmitAttempt(FORM_ID);
      await webToLeadRequest(
        {
          ...values,
          record_type: CUSTOMER_LEAD_RECORD_TYPE,
          comment: `
            -- COMMENT --
            Comment: ${values.comment}
    `,
        },
        webToLeadData
      );
      setContactFormSubmitted(true);
      resetForm({ values: '' });
      hideSuccessMessage();
      Mouseflow.notify.formSubmitSuccess(FORM_ID);
    } catch {
      Mouseflow.notify.formSubmitFailure(FORM_ID);
      Bugsnag.notify(
        new Error(
          `[Error trying to submit a webtolead form] with data: ${JSON.stringify(
            values
          )}`
        )
      );
    }
  };

  return (
    <>
      <Box px={3} pb={1}>
        <Box pb={1} textAlign={{ xs: 'center', md: 'left' }}>
          <Typography variant="h3" xs={{ lineHeight: 1.8 }}>
            <Trans
              t={t}
              i18nKey="customerLandingPage.requestForm.title"
              components={[
                <Typography
                  variant="inherit"
                  slab={!mobile}
                  component="inherit"
                  color={mobile ? 'inherit' : 'secondary'}
                  key="title"
                />,
              ]}
            />
          </Typography>
        </Box>
        <Typography
          variant="body2"
          mb={3}
          textAlign={{ xs: 'center', md: 'left' }}
        >
          {t('customerLandingPage.requestForm.subtitle')}
        </Typography>
        <Formik
          id={FORM_ID}
          initialValues={{
            first_name: '',
            last_name: '',
            email: '',
            caterer_name: '',
            phone: '',
          }}
          validationSchema={Yup.object({
            first_name: Yup.string().required(
              t('customerLandingPage.requestForm.isRequired')
            ),
            last_name: Yup.string().required(
              t('customerLandingPage.requestForm.isRequired')
            ),
            email: Yup.string()
              .required(t('customerLandingPage.requestForm.isRequired'))
              .email(t('customerLandingPage.requestForm.correctFormatEmail')),
            caterer_name: Yup.string().required(
              t('customerLandingPage.requestForm.isRequired')
            ),
            phone: Yup.string()
              .required(t('customerLandingPage.requestForm.isRequired'))
              .matches(
                PHONE_NUMBER_REGEX,
                t('customerLandingPage.requestForm.phoneError')
              ),
          })}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formikBag) => {
            return (
              <Form>
                <Box pb={1}>
                  <Grid container spacing={1}>
                    <Grid item md={6} xs={12}>
                      <Field
                        required
                        label={t('customerLandingPage.requestForm.firstName')}
                        placeholder={t(
                          'customerLandingPage.requestForm.firstName'
                        )}
                        name="first_name"
                        component={FormikFormField}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <Field
                        required
                        label={t('customerLandingPage.requestForm.lastName')}
                        placeholder={t(
                          'customerLandingPage.requestForm.lastName'
                        )}
                        name="last_name"
                        component={FormikFormField}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <Field
                        required
                        label={t('customerLandingPage.requestForm.email')}
                        placeholder={t('customerLandingPage.requestForm.email')}
                        name="email"
                        component={FormikFormField}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <Field
                        required
                        label={t('customerLandingPage.requestForm.company')}
                        placeholder={t(
                          'customerLandingPage.requestForm.company'
                        )}
                        name="caterer_name"
                        component={FormikFormField}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <Field
                        required
                        type="number"
                        label={t('customerLandingPage.requestForm.phone')}
                        placeholder={t('customerLandingPage.requestForm.phone')}
                        name="phone"
                        component={FormikFormField}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box display="flex" flexDirection={mobile ? 'column' : 'row'}>
                  <Box textAlign={{ xs: 'center', md: 'left' }}>
                    <Button
                      disabled={!formikBag.dirty}
                      size="lg"
                      variant="primary"
                      type="submit"
                      role="button"
                      aria-label="submit"
                      id="customer-page-request-submit"
                    >
                      {t('customerLandingPage.requestForm.submit')}
                    </Button>
                  </Box>
                  {contactFormSubmitted ? (
                    <Box alignSelf="center" pt={{ xs: 3, md: 0 }}>
                      <StyledBox>
                        <CheckIcon color="inherit" />
                        <Typography variant="caption" color="inherit" ml={1}>
                          {t('customerLandingPage.successMessage')}
                        </Typography>
                      </StyledBox>
                    </Box>
                  ) : null}
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </>
  );
}

const StyledBox = styled.div`
  display: flex;
  align-items: center;
  background-color: ${(props) => props.theme.palette.secondary.light};
  padding: ${(props) => props.theme.spacing(1, 1, 1, 1)};
  margin-left: ${(props) => props.theme.spacing(2)}px;
  width: 100%;
  border-radius: ${(props) => props.theme.spacing(1)}px;
  color: ${(props) => props.theme.palette.common.white};

  ${(props) => props.theme.breakpoints.down('sm')} {
    margin-left: 0;
  }
`;

export default RequestForm;
